import * as React from 'react'
import * as SwitchPrimitive from '@radix-ui/react-switch'
import { useController, Control, FieldValues, FieldPath } from 'react-hook-form'
import * as styles from './Switch.css'
import type { RootVariants } from './Switch.css'

// =============================================================================
// Types
// =============================================================================

type SwitchBaseProps = Omit<SwitchPrimitive.SwitchProps, 'checked' | 'onCheckedChange'> &
  NonNullable<RootVariants> & {
    /** Label text to display next to switch */
    label?: string
    /** Position of the label */
    labelPosition?: 'left' | 'right'
  }

// Props for standalone usage (no react-hook-form)
type SwitchStandaloneProps = SwitchBaseProps & {
  /** Current checked state */
  checked?: boolean
  /** Called when checked state changes */
  onCheckedChange?: (checked: boolean) => void
}

// Props for react-hook-form usage
type SwitchRHFProps<T extends FieldValues, N extends FieldPath<T>> = SwitchBaseProps & {
  /** Field name for react-hook-form */
  name: N
  /** Control object from useForm */
  control: Control<T>
}

export type SwitchProps<T extends FieldValues = FieldValues, N extends FieldPath<T> = FieldPath<T>> =
  | SwitchStandaloneProps
  | SwitchRHFProps<T, N>

// Type guard for RHF props
function isRHFProps<T extends FieldValues, N extends FieldPath<T>>(
  props: SwitchProps<T, N>
): props is SwitchRHFProps<T, N> {
  return 'name' in props && 'control' in props
}

// =============================================================================
// Component
// =============================================================================

function SwitchInner<T extends FieldValues, N extends FieldPath<T>>(
  props: SwitchProps<T, N>,
  ref: React.ForwardedRef<React.ComponentRef<typeof SwitchPrimitive.Root>>
) {
  const { label, labelPosition = 'right', size, className, disabled } = props

  // RHF integration
  let checked: boolean | undefined
  let onCheckedChange: ((checked: boolean) => void) | undefined
  let otherProps: Omit<SwitchBaseProps, 'label' | 'labelPosition' | 'size' | 'className' | 'disabled'>

  if (isRHFProps(props)) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { name, control, label: _l, labelPosition: _lp, size: _s, className: _c, disabled: _d, ...rest } = props
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { field } = useController({ name, control })
    checked = !!field.value
    onCheckedChange = field.onChange
    otherProps = rest
  } else {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { checked: c, onCheckedChange: oc, label: _l, labelPosition: _lp, size: _s, className: _c, disabled: _d, ...rest } = props as SwitchStandaloneProps
    checked = c
    onCheckedChange = oc
    otherProps = rest
  }

  const switchElement = (
    <SwitchPrimitive.Root
      ref={ref}
      className={`${styles.root({ size })} ${styles.switchRoot} ${className ?? ''}`}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      {...otherProps}
    >
      <SwitchPrimitive.Thumb className={styles.thumb({ size })} />
    </SwitchPrimitive.Root>
  )

  if (label) {
    return (
      <label className={styles.wrapper}>
        {labelPosition === 'left' && (
          <span className={styles.labelText} data-disabled={disabled ? '' : undefined}>
            {label}
          </span>
        )}
        {switchElement}
        {labelPosition === 'right' && (
          <span className={styles.labelText} data-disabled={disabled ? '' : undefined}>
            {label}
          </span>
        )}
      </label>
    )
  }

  return switchElement
}

// ForwardRef wrapper with generic support
const Switch = React.forwardRef(SwitchInner) as <
  T extends FieldValues = FieldValues,
  N extends FieldPath<T> = FieldPath<T>
>(
  props: SwitchProps<T, N> & { ref?: React.ForwardedRef<React.ComponentRef<typeof SwitchPrimitive.Root>> }
) => React.ReactElement

;(Switch as React.FC).displayName = 'Switch'

export { Switch }
